import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { setupSentry } from './utils/sentry';
import { installBestEffortPersistentStorage } from './services/persistent-storage';
import injectGlobals from './utils/inject-globals';
import { initializeCrossAppCache } from './utils/cross-app-cache';
import { selectSessionForMasq } from './utils/masq';
import { router } from './router';
import { installLinkPrefetching, registerRouteChunkPrefetch } from './services/router/prefetch';
import { registerSessionInvalidationRedirect } from './services/session/invalidation-redirect';
import { installRum } from './services/rum';
import { registerSiblingPreloads } from './routes/-sibling-preloads';
// Evaluated here, not with the first route chunk, so every `lazyRoute` page is in the
// prefetch registry before hover prefetch or a sibling preload asks for it.
import './routes/-route-pages';
import './index.scss';

setupSentry();
installBestEffortPersistentStorage();
installRum(router);
initializeCrossAppCache();
injectGlobals();
selectSessionForMasq();
installLinkPrefetching();
registerRouteChunkPrefetch(router);
registerSiblingPreloads();
registerSessionInvalidationRedirect();

/**
 * Dev only: `VITE_MOCK_CUSTOM_REPORTS=true` serves the custom reports endpoints, which the
 * backend does not have yet, from MSW. `import.meta.env.DEV` is false in a build, so the
 * import and msw are dropped from it.
 */
async function startDevMocks() {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_CUSTOM_REPORTS === 'true') {
    const { startCustomReportsDevWorker } = await import('@customerio/hydra-msw-mocks/browser');
    await startCustomReportsDevWorker();
  }
}

function renderApp() {
  const root = document.getElementById('root');
  if (root == null) throw new Error('Missing #root element');
  ReactDOM.createRoot(root, { identifierPrefix: 'hydra_' }).render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>,
  );
}

// `finally`, so a worker that fails to start still leaves a working app; the rejection is logged.
void startDevMocks().finally(renderApp);
